import React, { useRef, useState } from 'react';
import { Animated, StyleSheet } from 'react-native';
import PropTypes from 'prop-types';

import { Theme } from '@constants';

import { isTizen, isWeb } from '../../helpers/Platform';

/**
 * Headroom Component
 * An animation with a header component which hides when user scrolls down and shows the layout component.
 * Header reappears when user scrolls up.
 */
export const Headroom = ({
  headerComponent,
  layoutComponent,
  useNativeDriver,
  headerTestID,
  layoutTestID,
  layoutOffset,
  layoutValue,
  duration,
}) => {
  const headerOpacity = useRef(new Animated.Value(1)).current;
  const [isShowingHeader, setIsShowingHeader] = useState(true);

  const hideHeader = () => {
    setIsShowingHeader(false);
    // Move layout up
    const layoutAnimation = Animated.timing(layoutOffset, {
      toValue: 0,
      duration,
      useNativeDriver: true,
    });

    // Fade header out
    const headerAnimation = Animated.timing(headerOpacity, {
      toValue: 0,
      duration,
      useNativeDriver: true,
    });

    Animated.parallel([layoutAnimation, headerAnimation]).start();
  };

  const showHeader = () => {
    setIsShowingHeader(true);
    // Move layout down
    const layoutAnimation = Animated.timing(layoutOffset, {
      toValue: layoutValue,
      duration,
      useNativeDriver: true,
    });

    // Fade header in
    const headerAnimation = Animated.timing(headerOpacity, {
      toValue: 1,
      duration,
      useNativeDriver: true,
    });

    Animated.parallel([layoutAnimation, headerAnimation]).start();
  };

  /**
   * React.cloneElement used to clone and return a new headerComponent
   * with onFocus and isShowingHeader props from this component.
   */
  headerComponent = headerComponent
    ? React.cloneElement(headerComponent, {
        onFocus: showHeader,
        isShowingHeader,
      })
    : null;

  /**
   * React.cloneElement used to clone and return a new layoutComponent
   * with onLayoutFocus and isShowingHeader props from this component.
   */
  layoutComponent = layoutComponent
    ? React.cloneElement(layoutComponent, {
        onLayoutFocus: hideHeader,
        isShowingHeader,
      })
    : null;

  return (
    <>
      {headerComponent && (
        <Animated.View
          style={[styles.headerWrapper, { opacity: headerOpacity }]}
          testID={headerTestID}
          useNativeDriver={useNativeDriver}
        >
          {headerComponent}
        </Animated.View>
      )}
      {layoutComponent && (
        <Animated.View
          style={[styles.layoutWrapper, { transform: [{ translateY: layoutOffset }] }]}
          testID={layoutTestID}
          useNativeDriver={useNativeDriver}
        >
          {layoutComponent}
        </Animated.View>
      )}
    </>
  );
};

Headroom.propTypes = {
  headerComponent: PropTypes.element,
  layoutComponent: PropTypes.element,
  useNativeDriver: PropTypes.bool,
  headerTestID: PropTypes.string.isRequired,
  layoutTestID: PropTypes.string,
  layoutOffset: PropTypes.instanceOf(Animated.Value),
  layoutValue: PropTypes.number.isRequired,
  duration: PropTypes.number,
};

Headroom.defaultProps = {
  headerComponent: null,
  layoutComponent: null,
  useNativeDriver: true,
  layoutTestID: null,
  layoutOffset: Theme.appHeight,
  duration: 250,
};

const styles = StyleSheet.create({
  headerWrapper: {
    flex: 1,
    left: 0,
    position: isWeb && !isTizen ? 'fixed' : 'absolute',
    top: 0,
  },
  layoutWrapper:
    isWeb && !isTizen
      ? {
          flex: 1,
          position: 'fixed',
          left: 0,
          right: 0,
        }
      : {
          elevation: 5,
          flex: 1,
        },
});
